import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CircleNotch, ShieldStar, Prohibit, ArrowCounterClockwise, Trash, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";
import Logomark from "@/components/Logomark";
import UserMenu from "@/components/UserMenu";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const KICKER = "text-xs tracking-[0.25em] uppercase font-bold text-gray-500";

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.adminListUsers().then(setUsers).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

  const toggleBlock = async (u) => {
    setBusy(u.user_id);
    try {
      if (u.blocked) { await api.adminUnblockUser(u.user_id); toast.success(`Unblocked ${u.email}`); }
      else { await api.adminBlockUser(u.user_id); toast.success(`Blocked ${u.email}`); }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally { setBusy(null); }
  };

  const setTier = async (u, tier) => {
    setBusy(u.user_id);
    try {
      await api.adminSetTier(u.user_id, tier);
      toast.success(`${u.email} → ${tier === "full" ? "Full access" : "Free Taster"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally { setBusy(null); }
  };

  const doDelete = async () => {
    const u = confirmDel;
    setConfirmDel(null);
    if (!u) return;
    setBusy(u.user_id);
    try {
      await api.adminDeleteUser(u.user_id);
      toast.success(`Removed ${u.email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove user.");
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-gray-200 px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/library")} className="text-gray-500 hover:text-gray-900"><ArrowLeft size={20} /></button>
          <button onClick={() => navigate(user ? "/library" : "/")} className="flex items-center gap-2">
            <Logomark size={24} />
            <span className="font-display font-black text-2xl tracking-tight">Xartis</span>
          </button>
          <span className={`${KICKER} flex items-center gap-1.5`}><ShieldStar size={14} weight="duotone" /> Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto px-8 py-12">
        <p className={KICKER}>Account management</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none mt-2 mb-10">Users</h1>

        {users === null ? (
          <div className="flex items-center gap-3 text-gray-500 font-mono"><CircleNotch size={20} className="animate-spin" /> Loading…</div>
        ) : users.length === 0 ? (
          <div className="border border-dashed border-gray-300 py-20 text-center text-gray-500" data-testid="admin-empty">No users yet.</div>
        ) : (
          <div className="border border-gray-200 overflow-x-auto" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left font-mono text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Access</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Plans</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <motion.tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.2) }}
                    className="border-b border-gray-100 last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900 flex items-center gap-1.5">
                        {u.name || "—"}
                        {u.is_super_admin && <Crown size={14} weight="fill" className="text-amber-500" title="Super-admin" />}
                      </div>
                      <div className="text-gray-500 text-xs">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${u.role === "admin" ? "text-purple-800 border-purple-700 bg-purple-50" : "text-gray-600 border-gray-300 bg-gray-50"}`}>{u.role}</span>
                    </td>
                    <td className="px-4 py-3">
                      {u.role === "admin" ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border text-emerald-800 border-emerald-700 bg-emerald-50">Full</span>
                      ) : (
                        <button onClick={() => setTier(u, u.tier === "full" ? "free" : "full")} disabled={busy === u.user_id}
                          data-testid={`admin-tier-toggle-${u.user_id}`}
                          title={u.tier === "full" ? "Click to downgrade to Free Taster" : "Click to upgrade to Full access"}
                          className="inline-flex items-center gap-1.5 group">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${u.tier === "full" ? "text-emerald-800 border-emerald-700 bg-emerald-50" : "text-amber-800 border-amber-600 bg-amber-50"}`}>
                            {u.tier === "full" ? "Full" : "Free Taster"}
                          </span>
                          <span className="text-[10px] font-semibold text-gray-400 group-hover:text-gray-900 transition-colors">
                            {busy === u.user_id ? "…" : (u.tier === "full" ? "downgrade" : "upgrade")}
                          </span>
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${u.blocked ? "text-red-700 border-red-600 bg-red-50" : "text-emerald-800 border-emerald-700 bg-emerald-50"}`}>{u.blocked ? "Blocked" : "Active"}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-600">{u.plan_count}</td>
                    <td className="px-4 py-3 font-mono text-gray-500 text-xs">{fmt(u.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {u.is_super_admin ? (
                          <span className="text-xs text-gray-400 font-mono">protected</span>
                        ) : (
                          <>
                            <button onClick={() => toggleBlock(u)} disabled={busy === u.user_id}
                              data-testid={`admin-toggle-block-${u.user_id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-gray-300 hover:border-gray-900 transition-colors disabled:opacity-50">
                              {busy === u.user_id ? <CircleNotch size={14} className="animate-spin" /> : u.blocked ? <ArrowCounterClockwise size={14} /> : <Prohibit size={14} />}
                              {u.blocked ? "Unblock" : "Block"}
                            </button>
                            <button onClick={() => setConfirmDel(u)} disabled={busy === u.user_id}
                              data-testid={`admin-delete-${u.user_id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50">
                              <Trash size={14} /> Remove
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="rounded-none" data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel ? `"${confirmDel.email}" and all of their plans will be permanently deleted. This cannot be undone.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="admin-delete-confirm"
              className="rounded-none bg-red-600 hover:bg-red-700 text-white">Remove user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
